//---------------------------------------------------------------------------------------------------
// ## IMPORTS ##
// Library Imports
// Material ui Imports
// Custom components imports (and hooks and helpers)
import { useEffect, useState } from "react";
import { VTButton, VTSidePanel } from "@virtus-tech-repository/virtus-tech-repository";
import { EMedia, IMediaItem } from "@virtus-tech-repository/virtus-tech-repository/lib/models/media.model";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
    setCurrentDragging,
    setCurrentSidePanel,
    setEditHotspotDialogMediaTransfer,
} from "../../store/slices/current.slice";
import VTImmersiveVideoSidePanel from "../components/VTImmersiveVideoSidePanel";
import { EDragContentOptions } from "../../models/hotspotDrag.models";
import VTHotspotSidePanel from "../components/VTHotspotSidePanel";
import VTCreatorTabs from "../components/VTCreatorTabs";
import VTPanel from "../components/VTPanel";
import VTImmersiveImageSidePanel from "../components/VTImmersiveSidePanel";
import VTVideoSidePanel from "../components/VTVideoSidePanel";
import VTImageSidePanel from "../components/VTImageSidePanel";
import { VTAudioSidePanel } from "../components/VTAudioSidePanel";
import { IDragOptions } from "../../models/current.models";
import { Paper } from "@mui/material";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import VTTemplateSidePanel from "../components/VTTemplateSidePanel";
import VTImmersiveModelsSidePanel from "../components/VTImmersiveModelsSidePanel";
// Media imports

//---------------------------------------------------------------------------------------------------
// ## MUI STYLED ##
// Layout stuff remains inline, other styling pulled into it's own components

//---------------------------------------------------------------------------------------------------
// ## INTERFACES ##
interface IProps {
    handleCanvasDrop: any;
    handleQuestionCanvasDrop: any;
    handleTimelineDrop: any;
    videoCurrentState: "Paused" | "Playing" | "Question" | undefined;
    setVideoCurrentState: (videoCurrentState: "Paused" | "Playing" | "Question" | undefined) => void;
}

//---------------------------------------------------------------------------------------------------
// ## COMPONENTS ##
// Only display layouts in pages
export default function CreatorTabsPanelContainer({
    handleCanvasDrop,
    handleQuestionCanvasDrop,
    handleTimelineDrop,
    videoCurrentState,
    setVideoCurrentState,
}: IProps) {
    //-------------------------------------------------------------------------------------------------
    // ## HOOKS ##
    const dispatch = useAppDispatch();
    //-------------------------------------------------------------------------------------------------
    // ## USE SELECTOR ##
    const {
        currentSidePanel,
        editHotspotDialogIsCurrentlyOpen,
        editHotspotDialogTabCurrentlyOpen,
        currentHotspot,
        currentlyPreview,
    } = useAppSelector((state) => state.currentReducer);

    //-------------------------------------------------------------------------------------------------
    // ## USE STATE ##

    //-------------------------------------------------------------------------------------------------
    // ## USE EFFECTS ##
    useEffect(() => {
        if (editHotspotDialogTabCurrentlyOpen === "content" && currentSidePanel.currentTab) {
            setOpenDraw("Images");
        }
    }, [editHotspotDialogTabCurrentlyOpen]);

    //-------------------------------------------------------------------------------------------------
    // ## CUSTOM FUNCTIONS ##
    // Function to handle closing the draw via tab button
    function setCloseDraw() {
        dispatch(
            setCurrentSidePanel({
                currentTab: undefined,
                width: 0,
                option: undefined,
            }),
        );
    }

    // Function to handle opening the draw via tab button
    function setOpenDraw(drawName: string) {
        dispatch(
            setCurrentSidePanel({
                currentTab: drawName,
                width: 400,
                option: undefined,
            }),
        );
    }

    // Hook to hold the current drag item data
    function handleDragStart(mediaType: EDragContentOptions, src: string, preview?: string | undefined) {
        console.log("****************** dragging start", mediaType);
        dispatch(
            setCurrentDragging({
                mediaType: mediaType,
                src: src,
                preview: preview,
            }),
        );
    }

    // Hook to clear the current drag item data
    function handleDragStop() {
        dispatch(setCurrentDragging(undefined));
    }

    function handleEditPanelDoubleClick(Drag: IDragOptions, quesiton?: any) {
        dispatch(setEditHotspotDialogMediaTransfer({ src: Drag.src, type: Drag.mediaType }));
    }

    //-------------------------------------------------------------------------------------------------
    // ## RETURN ##
    // Show available images

    return (
        <>
            {/*-----------------------------------------------------------------------------------------*/}
            {/* Tab sections */}
            {!currentlyPreview && (
                <VTCreatorTabs
                    currentEditHotSpotDialogTabOpen={editHotspotDialogTabCurrentlyOpen}
                    isEditHotSpotDialogOpen={editHotspotDialogIsCurrentlyOpen}
                    setCloseDraw={setCloseDraw}
                    setOpenDraw={setOpenDraw}
                    disabled={["template", "video"]}
                />
            )}

            {/*-----------------------------------------------------------------------------------------*/}
            {/* Side panel with media content */}

            {!currentlyPreview && (
                <Paper
                    sx={{
                        position: "absolute",
                        top: "68px",
                        left: "100px",
                        bottom: "0px",
                        width: currentSidePanel.width,
                        zIndex: 1440,
                        borderRadius: 0,
                        backgroundColor: !currentSidePanel.width ? "#171717" : "#444547",
                        transition: "0.1s",
                    }}
                >
                    {currentSidePanel.width !== 0 && (
                        <VTButton
                            type={"icon"}
                            toolTipOptions={{
                                toolTipPlacement: "top-end",
                                toolTipText: "Collapse",
                            }}
                            sx={{
                                position: "absolute",
                                left: currentSidePanel.width,
                                top: currentHotspot.currentHotspot ? "80px" : "5.5px",
                                width: "30px",
                                height: "30px",
                                borderRadius: "0px 15px 15px 0px",
                                zIndex: 10,
                                backgroundColor: "#444",
                                fontSize: "0.3em",
                                ":hover": {
                                    cursor: "pointer",
                                },
                            }}
                            onClick={() =>
                                dispatch(
                                    setCurrentSidePanel({
                                        currentTab: undefined,
                                        width: 0,
                                        option: undefined,
                                    }),
                                )
                            }
                        >
                            <CloseRoundedIcon sx={{ fontSize: "3.5em", marginLeft: "-4px" }} />
                        </VTButton>
                    )}
                    {/*-------------------------------------------------------------------------------------*/}
                    {/* Template panel */}
                    {currentSidePanel.currentTab === "Templates" && (
                        <VTTemplateSidePanel
                            handleDragStart={handleDragStart}
                            handleDragStop={handleDragStop}
                            handleCanvasDrop={handleCanvasDrop}
                            handleQuestionCanvasDrop={handleQuestionCanvasDrop}
                            videoCurrentState={videoCurrentState}
                        />
                    )}

                    {/*-------------------------------------------------------------------------------------*/}
                    {/* Hotspot panel */}
                    {currentSidePanel.currentTab === "Hotspots" && (
                        <VTHotspotSidePanel
                            sideDrawWidth={currentSidePanel.width}
                            handleDragStart={handleDragStart}
                            handleDragStop={handleDragStop}
                            handleCanvasDrop={handleCanvasDrop}
                            handleQuestionCanvasDrop={handleQuestionCanvasDrop}
                            videoCurrentState={videoCurrentState}
                        />
                    )}

                    {/*-------------------------------------------------------------------------------------*/}
                    {/* 360 images for scene creation */}
                    {currentSidePanel.currentTab === "360 Images" && (
                        <VTImmersiveImageSidePanel
                            sideDrawWidth={currentSidePanel.width}
                            handleDragStart={handleDragStart}
                            handleDragStop={handleDragStop}
                            handleTimelineDrop={handleTimelineDrop}
                        />
                    )}

                    {/*-------------------------------------------------------------------------------------*/}
                    {/* Image panel as hotspot content */}
                    {/* TODO; Uncomment */}
                    {currentSidePanel.currentTab === "Images" && (
                        <VTImageSidePanel
                            sideDrawWidth={currentSidePanel.width}
                            handleDragStart={handleDragStart}
                            handleDragStop={handleDragStop}
                            imagesTitle={currentSidePanel.option ? currentSidePanel.option : ""}
                            handleCanvasDrop={
                                editHotspotDialogIsCurrentlyOpen ? handleEditPanelDoubleClick : handleCanvasDrop
                            }
                            handleQuestionCanvasDrop={
                                editHotspotDialogIsCurrentlyOpen ? handleEditPanelDoubleClick : handleQuestionCanvasDrop
                            }
                            videoCurrentState={videoCurrentState}
                        />
                    )}

                    {/*-------------------------------------------------------------------------------------*/}
                    {/* 360 videos for scene creation */}
                    {currentSidePanel.currentTab === "360 Videos" && (
                        <VTImmersiveVideoSidePanel
                            sideDrawWidth={currentSidePanel.width}
                            handleDragStart={handleDragStart}
                            handleDragStop={handleDragStop}
                            handleTimelineDrop={handleTimelineDrop}
                        />
                    )}

                    {/*-------------------------------------------------------------------------------------*/}
                    {/* videos for media input */}
                    {currentSidePanel.currentTab === "Videos" && (
                        <VTVideoSidePanel
                            sideDrawWidth={currentSidePanel.width}
                            handleDragStart={handleDragStart}
                            handleDragStop={handleDragStop}
                            handleCanvasDrop={
                                editHotspotDialogIsCurrentlyOpen ? handleEditPanelDoubleClick : handleCanvasDrop
                            }
                            handleQuestionCanvasDrop={
                                editHotspotDialogIsCurrentlyOpen ? handleEditPanelDoubleClick : handleQuestionCanvasDrop
                            }
                            videoCurrentState={videoCurrentState}
                        />
                    )}

                    {/*-------------------------------------------------------------------------------------*/}
                    {/* 3D models as hotspot content */}
                    {currentSidePanel.currentTab === "3D" && (
                        <VTImmersiveModelsSidePanel
                            sideDrawWidth={currentSidePanel.width}
                            handleDragStart={handleDragStart}
                            handleDragStop={handleDragStop}
                            imagesTitle={currentSidePanel.option ? currentSidePanel.option : ""}
                            handleCanvasDrop={
                                editHotspotDialogIsCurrentlyOpen ? handleEditPanelDoubleClick : handleCanvasDrop
                            }
                            handleQuestionCanvasDrop={
                                editHotspotDialogIsCurrentlyOpen ? handleEditPanelDoubleClick : handleQuestionCanvasDrop
                            }
                            videoCurrentState={videoCurrentState}
                        />
                    )}

                    {/*-------------------------------------------------------------------------------------*/}
                    {/* Audio content */}
                    {currentSidePanel.currentTab === "Audio" && (
                        <VTAudioSidePanel
                            onHandleDragStart={handleDragStart}
                            onHandleDragStop={handleDragStop}
                            handleCanvasDrop={
                                editHotspotDialogIsCurrentlyOpen ? handleEditPanelDoubleClick : handleCanvasDrop
                            }
                            handleQuestionCanvasDrop={
                                editHotspotDialogIsCurrentlyOpen ? handleEditPanelDoubleClick : handleQuestionCanvasDrop
                            }
                            videoCurrentState={videoCurrentState}
                        />
                    )}
                </Paper>
            )}
        </>
    );
}
